/**
 * Friendly-label resolution for workflow identifiers (UI-consistency spec
 * 2026-07-11 §2). One module owns token→label, capability→label, and the
 * humanize fallback so the Overview tabs, Messages chips, Automations
 * sentences, and editor legends can never drift apart.
 */

/** `$slots.checkout.formatted_total`-style placeholders (≥ one dot). */
export const TOKEN_RE = /\$[a-z_]+(?:\.[a-z0-9_]+)+/g

/** Labels for computed/nested fields the definition carries no label for.
 * Keyed by the path AFTER the `$slots.` prefix. */
const TOKEN_LABELS: Record<string, string> = {
  'checkout.formatted_total': 'Total',
  'checkout.total_cents': 'Total (cents)',
  'checkout.items_description': 'Items',
  'checkout.line_description': 'Order line',
  'checkout.formatted_delivery': 'Delivery fee',
  'checkout.currency': 'Currency',
  'cart.formatted': 'Cart subtotal',
  'cart.item_count': 'Item count',
  'cart.items': 'Cart items',
  'delivery_quote.total_cents': 'Delivery quote (cents)',
  'delivery_booking.share_link': 'Delivery tracking link',
  'payment_link.url': 'Payment link',
  'payment_link.id': 'Payment link ID',
  'bank_transfer.bank_name': 'Bank name',
  'bank_transfer.beneficiary_name': 'Beneficiary name',
  'bank_transfer.account_or_clabe': 'Account / CLABE',
  'bank_transfer.formatted_amount': 'Transfer amount',
  'bank_transfer.transfer_id': 'Transfer ID',
  'geo.formatted_address': 'Address',
  'geo.lat': 'Latitude',
  'geo.lng': 'Longitude',
  'pickup.formatted_list': 'Pickup locations',
  'caps.fulfillment_text': 'Fulfillment options',
  delivery_method_display: 'Delivery method',
}

const CAPABILITY_LABELS: Record<string, string> = {
  'catalog.list': 'Look up the catalog',
  'checkout.summary': 'Build the order summary',
  'delivery.quote': 'Quote delivery',
  'geo.geocode': 'Locate the address',
  'inventory.check_availability': 'Check availability',
  'inventory.reserve': 'Reserve inventory',
  'order.context': 'Load order context',
  'payments.bank_transfer.create': 'Create bank-transfer instructions',
  'payments.bank_transfer.cancel': 'Cancel the bank transfer',
  'payments.create_link': 'Create a payment link',
  'payments.deactivate_link': 'Deactivate the payment link',
  'pickup.lookup': 'Look up pickup locations',
}

/** 'quote_summary' → 'Quote summary'. */
export function humanize(segment: string): string {
  const words = segment.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Resolution order (spec §2): definition slot label → dictionary →
 * humanized last segment. Never returns raw `$slots.` text.
 */
export function tokenLabel(
  token: string,
  slotLabels?: Record<string, string>,
): string {
  const path = token.startsWith('$slots.') ? token.slice(7) : token.slice(1)
  const parts = path.split('.')
  if (parts.length === 1 && slotLabels?.[parts[0]]) return slotLabels[parts[0]]
  if (TOKEN_LABELS[path]) return TOKEN_LABELS[path]
  return humanize(parts[parts.length - 1])
}

export function capabilityLabel(id: string): string {
  if (CAPABILITY_LABELS[id]) return CAPABILITY_LABELS[id]
  const parts = id.split('.')
  return humanize(parts[parts.length - 1])
}

/** Unique tokens across a lang→text map, in first-seen order. */
export function extractTokens(texts: Record<string, string>): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const value of Object.values(texts)) {
    for (const m of value.matchAll(TOKEN_RE)) {
      if (!seen.has(m[0])) {
        seen.add(m[0])
        out.push(m[0])
      }
    }
  }
  return out
}
